// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@ces/App/Store/Reducers";
import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@ces/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: CustomAppState) => {
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const checkoutReviewAndSubmitPageUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
    const referredFromCheckout = returnUrl === shippingPageUrl || returnUrl === checkoutReviewAndSubmitPageUrl;

    return {
        checkoutView:
            checkoutPages.includes(getCurrentPage(state).type) ||
            getCurrentPage(state).type === "RfqCustomRequestQuotePage" ||
            (referredFromCheckout && getCurrentPage(state).type === "SignInPage"),
        isAuthenticated: xIsAuthenticated(state),
    };
};
interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    bottomSection?: InjectableCss;
    workingHoursWrapper?: InjectableCss;
    linksWrapper?: InjectableCss;
    copyrightText?: TypographyProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

// XNG-Changes: change structure
const FooterContainer: FC<Props> = ({ id, extendedStyles, isAuthenticated, checkoutView }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const isEditing = useShellContext().isEditing;

    return (
        <StyledWrapper {...styles.widgetGridContainer} {...{ isAuthenticated, checkoutView }}>
            {!checkoutView && (
                <StyledWrapper
                    {...styles.subscribeGridItem}
                    className={`footer-subscribe ${isEditing ? "CMS-Mode" : ""}`}
                >
                    <Zone zoneName="Subscribe" contentId={id} />
                </StyledWrapper>
            )}

            <StyledWrapper className="grid-container">
                {!checkoutView && (
                    <>
                        <StyledWrapper {...styles.leftSection} className="leftSection">
                            <StyledWrapper {...styles.logoWrapper} className={isEditing ? "CMS-Mode" : ""}>
                                <Zone zoneName="Logo" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.workingHoursWrapper}>
                                <Zone zoneName="WorkingHours" contentId={id} />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.linksWrapper} className="linksWrapper">
                            <StyledWrapper {...styles.linkList1GridItem}>
                                <Zone zoneName="LinkList1" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList2GridItem}>
                                <Zone zoneName="LinkList2" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList3GridItem}>
                                <Zone zoneName="LinkList3" contentId={id} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList4GridItem}>
                                <Zone zoneName="LinkList4" contentId={id} />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.socialLinksGridItem}
                            className={isEditing ? "CMS-Mode" : "socialLinksWrapper"}
                        >
                            <Zone zoneName="Sponsers" contentId={id} />
                            <Zone zoneName="SocialLinks" contentId={id} />
                        </StyledWrapper>
                    </>
                )}
                <StyledWrapper {...styles.bottomSection} className="footer-bottom-section">
                    <Typography {...styles.copyrightText}>
                        {siteMessage("Custom_Footer_Copyright", String(new Date().getFullYear()))}
                    </Typography>
                    <StyledWrapper {...styles.siteMapLinksGridItem}>
                        <Zone zoneName="SiteMapLinks" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: connect(mapStateToProps)(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
